import { object, string } from 'yup';
import { OperatorsAndVehiclesFormValues } from './types';
import i18n from '../../../../i18n';

/**
 * Minuto de una fecha (ms / 60 000, redondeado hacia abajo) o `null` si no hay
 * fecha o no se entiende. Las reglas de orden comparan AL MINUTO (3-nov): la
 * pantalla solo deja elegir HH:MM, pero el inicio puede llevar segundos (el de
 * una actuación nueva sale de `new Date()`; el de la básica lo calcula el
 * recorrido). En milisegundos, una llegada a las 12:49 «era anterior» a un
 * inicio que en pantalla también dice 12:49 (At.0000807.0002.03 en beta).
 * Mismo criterio que `calculateHourDifference`, que pone los segundos a 0.
 */
const minuteOf = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 60_000);
};

/**
 * El final no puede ser anterior al inicio (Bug #13); el mismo minuto vale.
 * Solo con las dos fechas. Lo comparten Operarios, `checkSpecialValues` y el
 * horario de la básica en «Datos Act».
 */
export const isEndNotBeforeStart = (
  end?: string | null,
  start?: string | null,
): boolean => {
  const endMinute = minuteOf(end);
  const startMinute = minuteOf(start);
  if (endMinute === null || startMinute === null) return true;
  return endMinute >= startMinute;
};

/**
 * La llegada al tajo va entre el inicio y el final: tiene que ser ESTRICTAMENTE
 * anterior a la fecha final (en minutos: 18:00 no es anterior a 18:00:30).
 * Solo se compara con las dos fechas presentes — la llegada es opcional y el
 * final puede seguir vacío con la actuación abierta. Lo comparten el esquema
 * (modal de errores del asistente) y la comprobación al volver a la pestaña
 * (`checkSpecialValues`); también el horario de la Actuació bàsica en «Datos Act».
 */
export const isArrivalBeforeEnd = (
  arrival?: string | null,
  end?: string | null,
): boolean => {
  const arrivalMinute = minuteOf(arrival);
  const endMinute = minuteOf(end);
  if (arrivalMinute === null || endMinute === null) return true;
  return arrivalMinute < endMinute;
};

/**
 * Y la llegada tampoco puede ser ANTERIOR al inicio (3-nov, petición del
 * usuario). El mismo minuto SÍ vale: el equipo puede estar ya en el sitio al
 * empezar. Solo se compara con las dos fechas presentes (la llegada es
 * opcional). La comparten los mismos sitios que `isArrivalBeforeEnd`.
 */
export const isArrivalNotBeforeStart = (
  arrival?: string | null,
  start?: string | null,
): boolean => {
  const arrivalMinute = minuteOf(arrival);
  const startMinute = minuteOf(start);
  if (arrivalMinute === null || startMinute === null) return true;
  return arrivalMinute >= startMinute;
};

// Schema dinámico que hace hoursType condicional basado en feature flag
// BUG #20 FIX COMPLETO: Usar .nullable().optional() para aceptar tanto null como undefined
// Yup's .optional() solo acepta undefined, NO null. Cuando showHoursType=false,
// el campo puede tener valor null (de initialData) y debe ser aceptado.
export const createOperatorsAndVehicleSchema = (showHoursType: boolean) => {
  return object<OperatorsAndVehiclesFormValues>({
    registerInitDate: string().required(() => i18n.t('validation:required')),
    // Bug #13 — si el usuario rellena la fecha final antes que la inicio, o se
    // confunde y mete una hora anterior, el cálculo de minutosTotales daba
    // negativos y se propagaba a Action.minutesTotals en BD. Aquí impedimos
    // el submit cuando endDate < startDate, y en `calculateHourDifference`
    // añadimos Math.abs como defensa en profundidad.
    registerEndDate: string()
      .optional()
      .test(
        'end-after-start',
        () => i18n.t('validation:endAfterStart'),
        function (value) {
          const { registerInitDate } = this.parent as {
            registerInitDate?: string;
          };
          return isEndNotBeforeStart(value, registerInitDate);
        },
      ),
    hoursType: showHoursType
      ? string().required(() => i18n.t('validation:required'))
      : string().nullable().optional(),
    // Hora de llegada: opcional, entre el inicio (igual vale) y la fecha final
    // (estrictamente antes). No afecta al cálculo de horas (totalHours sigue
    // saliendo de registerInitDate/registerEndDate).
    registerArrivalDate: string()
      .nullable()
      .optional()
      .test(
        'arrival-after-start',
        () => i18n.t('validation:arrivalAfterStart'),
        function (value) {
          const { registerInitDate } = this.parent as {
            registerInitDate?: string;
          };
          return isArrivalNotBeforeStart(value, registerInitDate);
        },
      )
      .test(
        'arrival-before-end',
        () => i18n.t('validation:arrivalBeforeEnd'),
        function (value) {
          const { registerEndDate } = this.parent as {
            registerEndDate?: string;
          };
          return isArrivalBeforeEnd(value, registerEndDate);
        },
      ),
    totalHours: string().required(() => i18n.t('validation:required')),
  });
};

// Export legacy para compatibilidad con otros posibles usos
export const OperatorsAndVehicleSchema = createOperatorsAndVehicleSchema(true);
